import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Lock, LockOpen, Upload, Loader2, ShieldCheck } from "lucide-react";

const AvatarCircle = ({ profile, size = "h-16 w-16", testid }) => (
  profile?.avatar ? (
    <img src={profile.avatar.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${profile.avatar}` : profile.avatar} alt="" className={`${size} rounded-full object-cover object-top border border-nx-gold/50`} data-testid={testid} />
  ) : (
    <span className={`${size} rounded-full border border-white/15 bg-nx-navy2 flex items-center justify-center font-marcellus text-lg text-nx-goldlight`} data-testid={testid}>
      {(profile?.email || "?")[0].toUpperCase()}
    </span>
  )
);

export const ProfileModal = ({ api, profile, onClose, onUpdated }) => {
  const [staff, setStaff] = useState(null);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const adminFileRef = useRef({});
  const isAdmin = profile.role === "admin";

  const loadStaff = async () => {
    try {
      const { data } = await api.get("/mail/admin/staff");
      setStaff(data.staff);
    } catch {
      toast.error("Could not load team accounts");
    }
  };

  useEffect(() => {
    if (isAdmin) loadStaff();
  }, [isAdmin]);

  const upload = async (file, targetEmail) => {
    if (!file) return;
    setBusy(targetEmail || "self");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (targetEmail) {
        fd.append("email", targetEmail);
        await api.post("/mail/admin/avatar", fd);
        await loadStaff();
        toast.success(`Profile picture updated for ${targetEmail}`);
      } else {
        await api.post("/mail/profile/avatar", fd);
        toast.success("Profile picture updated");
      }
      onUpdated();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed — try another image");
    } finally {
      setBusy("");
    }
  };

  const toggleLock = async (email, locked) => {
    setBusy(`lock-${email}`);
    try {
      await api.post("/mail/admin/avatar-lock", { email, locked });
      await loadStaff();
      onUpdated();
      toast.success(locked ? `Picture locked for ${email}` : `Picture unlocked for ${email}`);
    } catch {
      toast.error("Could not change the lock");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-lenis-prevent className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" data-testid="profile-modal">
      <div className="bg-nx-navy2 border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 sm:p-7">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-marcellus text-lg text-white">Profile Picture</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1" aria-label="Close" data-testid="profile-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="flex items-center gap-5">
          <AvatarCircle profile={profile} testid="profile-avatar-img" />
          <div className="min-w-0">
            <p className="text-sm text-white truncate">{profile.email}</p>
            <p className="text-[11px] text-slate-500 mt-0.5 capitalize">{profile.role} account</p>
          </div>
        </div>

        {profile.avatar_locked ? (
          <p className="mt-5 flex items-center gap-2 text-xs text-amber-300 border border-amber-500/40 px-4 py-3" data-testid="profile-locked-message">
            <Lock size={13} /> Your profile picture is managed by the administrator.
          </p>
        ) : (
          <div className="mt-5">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="profile-upload-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
            <button onClick={() => fileRef.current?.click()} disabled={!!busy} className="flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.12em] uppercase px-5 py-3 transition-colors disabled:opacity-60" data-testid="profile-upload-button">
              {busy === "self" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {profile.avatar ? "Change photo" : "Upload photo"}
            </button>
            <p className="text-[11px] text-slate-500 mt-2">JPG or PNG, up to 5 MB. Shown across NexSecure Mail.</p>
          </div>
        )}

        {isAdmin && (
          <div className="mt-8 border-t border-white/10 pt-6" data-testid="admin-staff-list">
            <p className="text-[10px] tracking-[0.22em] uppercase text-slate-500 flex items-center gap-2 mb-4">
              <ShieldCheck size={13} className="text-nx-goldlight" /> Team photos — manage any account
            </p>
            <form className="flex gap-2 mb-4" onSubmit={async (e) => {
              e.preventDefault();
              const el = e.target.elements.newMailbox;
              const email = el.value.trim();
              if (!email) return;
              setBusy("add");
              try {
                await api.post("/mail/admin/add-mailbox", { email });
                el.value = "";
                await loadStaff();
                toast.success(`${email.toLowerCase()} added to the list`);
              } catch (err) {
                const d = err?.response?.data?.detail;
                toast.error(typeof d === "string" ? d : "Could not add that account");
              } finally {
                setBusy("");
              }
            }}>
              <input name="newMailbox" type="email" required placeholder="new@nexsecure.in" className="flex-1 bg-nx-navy border border-white/15 px-3 py-2 text-xs text-white placeholder:text-slate-600 focus:border-nx-gold/60 focus:outline-none" data-testid="admin-add-mailbox-input" />
              <button type="submit" disabled={!!busy} className="text-[10px] tracking-[0.1em] uppercase bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold px-4 py-2 transition-colors disabled:opacity-60" data-testid="admin-add-mailbox-button">
                {busy === "add" ? <Loader2 size={11} className="animate-spin" /> : "Add account"}
              </button>
            </form>
            {staff === null ? (
              <p className="text-xs text-slate-500 flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading accounts…</p>
            ) : (
              <div className="flex flex-col gap-2">
                {staff.map((s) => (
                  <div key={s.email} className="flex items-center gap-3 border border-white/10 bg-white/[0.02] px-4 py-3" data-testid={`admin-staff-row-${s.email}`}>
                    <AvatarCircle profile={s} size="h-10 w-10" testid={`admin-avatar-img-${s.email}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-white truncate">{s.email}</p>
                      <p className="text-[10px] text-slate-500 capitalize">{s.role}{s.avatar_locked ? (s.avatar_locked_by === "ceo@nexsecure.in" ? " · locked by CEO" : " · picture locked") : ""}</p>
                    </div>
                    <input ref={(el) => { adminFileRef.current[s.email] = el; }} type="file" accept="image/*" className="hidden" data-testid={`admin-avatar-input-${s.email}`} onChange={(e) => { upload(e.target.files?.[0], s.email); e.target.value = ""; }} />
                    <button onClick={() => adminFileRef.current[s.email]?.click()} disabled={!!busy} className="text-[10px] tracking-[0.1em] uppercase border border-white/15 text-slate-300 hover:border-nx-gold/60 hover:text-nx-goldlight px-3 py-2 transition-colors disabled:opacity-60" data-testid={`admin-avatar-upload-${s.email}`}>
                      {busy === s.email ? <Loader2 size={11} className="animate-spin" /> : "Set photo"}
                    </button>
                    <button onClick={() => toggleLock(s.email, !s.avatar_locked)} disabled={!!busy} className={`flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase border px-3 py-2 transition-colors disabled:opacity-60 ${s.avatar_locked ? "border-amber-500/50 text-amber-300" : "border-white/15 text-slate-400 hover:border-nx-gold/60 hover:text-nx-goldlight"}`} data-testid={`admin-lock-toggle-${s.email}`}>
                      {busy === `lock-${s.email}` ? <Loader2 size={11} className="animate-spin" /> : s.avatar_locked ? <><Lock size={11} /> Locked</> : <><LockOpen size={11} /> Unlocked</>}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
